import {clearDiv} from "../helpers/builders.js";

// Shared shaper navigation renderer (US-001).
//
// Live pages fetch /api/treeNavigationSingle/{hash}/{node_id} and post the
// payload to the dashboard via onMessage; training pages read the same shape
// from a snapshot blob. Both funnel through the helpers below so the live and
// training navigators stay visually and behaviourally identical.

const TRAIL_HEAD = 2;
const TRAIL_TAIL = 2;
const TRAIL_COLLAPSE_AT = 5;
const CHILD_INLINE_CAP = 10;

// Build page URLs for the live or training site, keeping the shaper node id.
function navigationUrls(mode) {
    const base = mode === "training" ? "/training/" : "";
    return {
        shaper: (nodeId) => base + "shaper.html?shaper_id=" + encodeURIComponent(nodeId ?? ""),
        site: (hash, nodeId) => base + "shaper_site.html?site=" + encodeURIComponent(hash) + "&nodeId=" + encodeURIComponent(nodeId ?? ""),
        circuit: (hash, nodeId) => base + "shaper_circuit.html?circuit=" + encodeURIComponent(hash) + "&nodeId=" + encodeURIComponent(nodeId ?? ""),
    };
}

// Normalise the API payload: {root, layers, destinations, circuit}.
// Layers are ordered root -> current; the last layer is the current node.
// Destinations are the current node's child sites and circuits.
// When nodeId is known, the payload is pinned to the path whose
// ShaperRoot.shaper_id matches it, and destinations are read only from that
// path.
export function parseNavigation(paths, nodeId) {
    const root = {name: "Shaper"};
    const layers = [];
    const destinations = [];
    let circuit = null;

    const list = Array.isArray(paths) ? paths.filter((path) => Array.isArray(path)) : [];
    // A multi-shaper license can yield one path per shaper. Pin to the path
    // that names this page's shaper so another shaper's hierarchy (for
    // example under a duplicate node_name) cannot leak into this view.
    const selectedPath = nodeId
        ? list.find((path) => path.some((item) => item && item.ShaperRoot && String(item.ShaperRoot.shaper_id) === String(nodeId)))
        : undefined;
    const trailPath = selectedPath ?? list.reduce(
        (best, path) => (best === null || path.length > best.length ? path : best),
        null,
    );

    (trailPath || []).forEach((item) => {
        if (!item) return;
        if (item.ShaperRoot) {
            if (item.ShaperRoot.name) root.name = item.ShaperRoot.name;
        } else if (item.Layer) {
            layers.push({name: item.Layer.name, hash: item.Layer.hash});
        } else if (item.Circuit) {
            circuit = {name: item.Circuit.name, hash: item.Circuit.hash};
        }
    });

    (trailPath || []).forEach((item) => {
        if (!item || !item.Destinations || !Array.isArray(item.Destinations.destinations)) return;
        item.Destinations.destinations.forEach((dest) => {
            if (dest.Site) {
                destinations.push({kind: "site", name: dest.Site.name, hash: dest.Site.hash});
            } else if (dest.Circuit) {
                destinations.push({kind: "circuit", name: dest.Circuit.name, hash: dest.Circuit.hash});
            }
        });
    });

    return {root, layers, destinations, circuit};
}

// Build the ordered crumb list from parsed navigation data.
export function buildTrail(parsed, nodeId, urls) {
    const {root, layers, circuit} = parsed;
    const trail = [];
    const rootIsCurrent = layers.length === 0 && !circuit;
    trail.push({name: root.name, current: rootIsCurrent, href: urls.shaper(nodeId)});
    const lastLayerIsCurrent = !circuit && layers.length > 0;
    layers.forEach((layer, index) => {
        const isCurrent = lastLayerIsCurrent && index === layers.length - 1;
        trail.push({
            name: layer.name,
            current: isCurrent,
            href: urls.site(layer.hash, nodeId),
        });
    });
    if (circuit) {
        trail.push({name: circuit.name, current: true, href: null});
    }
    return trail;
}

function createSeparator() {
    const sep = document.createElement("i");
    sep.className = "fa fa-chevron-right lqos-nav-crumb-sep";
    sep.setAttribute("aria-hidden", "true");
    return sep;
}

function createFlatMessage() {
    const flatMsg = document.createElement("span");
    flatMsg.className = "text-muted small";
    flatMsg.textContent = "Flat network: use search to find circuits.";
    return flatMsg;
}

// Render a single breadcrumb: current nodes are plain text, ancestors are links.
function renderCrumb(crumb) {
    let el;
    if (crumb.current || !crumb.href) {
        el = document.createElement("span");
        el.className = "lqos-nav-crumb lqos-nav-crumb-current redactable";
        el.setAttribute("aria-current", "page");
    } else {
        el = document.createElement("a");
        el.className = "lqos-nav-crumb redactable";
        el.href = crumb.href;
    }
    el.textContent = crumb.name;
    el.title = crumb.name;
    return el;
}

// Collapse the middle of a long trail into a keyboard-accessible dropdown.
function buildCollapseMenu(items) {
    const wrap = document.createElement("span");
    wrap.className = "dropdown lqos-nav-collapse-wrap";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "lqos-nav-collapse";
    button.setAttribute("data-bs-toggle", "dropdown");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-label", "Show " + items.length + " hidden path steps");
    button.title = "Show hidden path steps";
    button.textContent = "…";
    wrap.appendChild(button);

    const menu = document.createElement("ul");
    menu.className = "dropdown-menu";
    items.forEach((crumb) => {
        const li = document.createElement("li");
        // Collapsed crumbs sit before the trailing two entries, so every one is
        // a linked ancestor.
        const link = document.createElement("a");
        link.className = "dropdown-item redactable";
        link.href = crumb.href;
        link.textContent = crumb.name;
        li.appendChild(link);
        menu.appendChild(li);
    });
    wrap.appendChild(menu);
    return wrap;
}

function buildBreadcrumbTrail(trail) {
    const container = document.createElement("nav");
    container.className = "lqos-nav-breadcrumb";
    container.setAttribute("aria-label", "Shaper location");

    // The leading separator belongs to the trail so it wraps with the crumbs
    // instead of being stranded next to the shaper picker.
    container.appendChild(createSeparator());

    const nodes = [];
    if (trail.length <= TRAIL_COLLAPSE_AT) {
        trail.forEach((crumb) => nodes.push({type: "crumb", crumb}));
    } else {
        trail.slice(0, TRAIL_HEAD).forEach((crumb) => nodes.push({type: "crumb", crumb}));
        nodes.push({
            type: "collapse",
            items: trail.slice(TRAIL_HEAD, trail.length - TRAIL_TAIL),
        });
        trail.slice(trail.length - TRAIL_TAIL).forEach((crumb) => nodes.push({type: "crumb", crumb}));
    }

    nodes.forEach((node, index) => {
        if (index > 0) container.appendChild(createSeparator());
        container.appendChild(
            node.type === "collapse" ? buildCollapseMenu(node.items) : renderCrumb(node.crumb),
        );
    });
    return container;
}

// One-click access to the current node's child sites and circuits.
function buildChildChip(dest, nodeId, urls) {
    const link = document.createElement("a");
    link.className = "lqos-nav-chip redactable";
    link.href = dest.kind === "site"
        ? urls.site(dest.hash, nodeId)
        : urls.circuit(dest.hash, nodeId);
    link.title = (dest.kind === "site" ? "Site: " : "Circuit: ") + dest.name;

    const icon = document.createElement("i");
    icon.className = "fa " + (dest.kind === "site" ? "fa-sitemap" : "fa-network-wired");
    icon.setAttribute("aria-hidden", "true");
    link.appendChild(icon);
    link.appendChild(document.createTextNode(dest.name));
    return link;
}

// Overflow children move into a labelled dropdown so a large branch (e.g. the
// shaper root with ~33 children) does not push the dashboard below the fold.
function buildChildOverflowMenu(overflow, nodeId, urls) {
    const wrap = document.createElement("span");
    wrap.className = "dropdown lqos-nav-children-more-wrap";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "lqos-nav-children-more";
    button.setAttribute("data-bs-toggle", "dropdown");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-label", "Show the remaining " + overflow.length + " children");
    button.title = "Show the remaining " + overflow.length + " children";
    button.appendChild(document.createTextNode("Children (" + overflow.length + ") "));
    const caret = document.createElement("i");
    caret.className = "fa fa-chevron-down ms-1";
    caret.setAttribute("aria-hidden", "true");
    button.appendChild(caret);
    wrap.appendChild(button);

    const menu = document.createElement("ul");
    menu.className = "dropdown-menu";
    overflow.forEach((dest) => {
        const li = document.createElement("li");
        const link = document.createElement("a");
        link.className = "dropdown-item redactable";
        link.href = dest.kind === "site"
            ? urls.site(dest.hash, nodeId)
            : urls.circuit(dest.hash, nodeId);
        const icon = document.createElement("i");
        icon.className = "fa " + (dest.kind === "site" ? "fa-sitemap" : "fa-network-wired") + " me-2";
        icon.setAttribute("aria-hidden", "true");
        link.appendChild(icon);
        link.appendChild(document.createTextNode(dest.name));
        li.appendChild(link);
        menu.appendChild(li);
    });
    wrap.appendChild(menu);
    return wrap;
}

function buildChildren(destinations, nodeId, urls) {
    const container = document.createElement("div");
    container.className = "lqos-nav-children";

    const label = document.createElement("span");
    label.className = "lqos-nav-children-label";
    label.textContent = "Children";
    container.appendChild(label);

    const inline = destinations.length > CHILD_INLINE_CAP
        ? destinations.slice(0, CHILD_INLINE_CAP)
        : destinations;
    const overflow = destinations.length > CHILD_INLINE_CAP
        ? destinations.slice(CHILD_INLINE_CAP)
        : [];

    inline.forEach((dest) => container.appendChild(buildChildChip(dest, nodeId, urls)));
    if (overflow.length > 0) {
        container.appendChild(buildChildOverflowMenu(overflow, nodeId, urls));
    }
    return container;
}

// Append the breadcrumb trail (and, when present, the child destinations).
function appendBreadcrumb(wrap, paths, nodeId, mode) {
    const urls = navigationUrls(mode);
    const parsed = parseNavigation(paths, nodeId);
    const trail = buildTrail(parsed, nodeId, urls);
    wrap.appendChild(buildBreadcrumbTrail(trail));
    if (parsed.destinations.length > 0) {
        wrap.appendChild(buildChildren(parsed.destinations, nodeId, urls));
    }
}

// Shaper picker: live fetches the shaper list; training shows a single entry.
function buildShaperPicker(mode, nodeId, rootName) {
    const container = document.createElement("span");
    container.className = "lqos-shaper-picker me-2";

    const urls = navigationUrls(mode);
    const labelLink = document.createElement("a");
    labelLink.className = "me-1 small text-muted text-decoration-none shaper-root-link";
    labelLink.href = urls.shaper(nodeId);
    labelLink.title = "Go to shaper overview";
    const labelIcon = document.createElement("i");
    labelIcon.className = "fa fa-sitemap me-1";
    labelLink.appendChild(labelIcon);
    labelLink.appendChild(document.createTextNode("Shaper"));
    const colon = document.createTextNode(":");

    const select = document.createElement("select");
    select.className = "form-select form-select-sm d-inline-block";
    select.style.width = "auto";
    select.setAttribute("aria-label", "Select shaper server");

    if (mode === "training") {
        const opt = document.createElement("option");
        opt.value = nodeId ?? "";
        opt.text = rootName || "Shaper";
        select.add(opt);
    } else {
        const loading = document.createElement("option");
        loading.text = "Loading…";
        loading.value = "";
        select.add(loading);
        select.disabled = true;
        $.get("/api/integrations/shaperList", (shapers) => {
            while (select.options.length) select.remove(0);
            shapers.forEach((s) => {
                const opt = document.createElement("option");
                opt.value = s.node_id;
                opt.text = s.node_name + (s.role ? ` (${s.role})` : "");
                select.add(opt);
            });
            if (nodeId) select.value = String(nodeId);
            select.disabled = false;
        });
        select.addEventListener("change", (e) => {
            const value = e.target.value;
            if (!value) return;
            const href = window.location.href;
            if (href.indexOf("shaper_site.html") > -1) {
                const params = new URLSearchParams(window.location.search);
                const site = params.get("site") ?? window.siteId;
                if (site) {
                    window.location.href = urls.site(site, value);
                    return;
                }
            } else if (href.indexOf("shaper_circuit.html") > -1) {
                const params = new URLSearchParams(window.location.search);
                const circuit = params.get("circuit") ?? window.circuitId;
                if (circuit) {
                    window.location.href = urls.circuit(circuit, value);
                    return;
                }
            }
            window.location.href = urls.shaper(value);
        });
    }

    container.appendChild(labelLink);
    container.appendChild(colon);
    container.appendChild(select);
    return container;
}

function createNavWrap(nav) {
    const wrap = document.createElement("div");
    wrap.className = "lqos-shaper-nav";
    nav.appendChild(wrap);
    return wrap;
}

export function setupNavigationOneShaper(hash, nodeId, onMessage = null) {
    const nav = document.getElementById("navigator");
    let wrap = null;
    if (nav) {
        clearDiv(nav);
        wrap = createNavWrap(nav);
        wrap.appendChild(buildShaperPicker("live", nodeId));
    }

    $.get("/api/treeNavigationSingle/" + hash + "/" + nodeId, (data) => {
        const paths = data.paths || data;
        const isFlat = !!data.is_flat;
        window.isFlatNetwork = isFlat;
        if (onMessage !== null) {
            onMessage({
                Data: {
                    target: "shaper_navigation",
                    blob: paths,
                    is_flat: isFlat,
                },
            });
        }
        if (!wrap) return;
        if (isFlat) {
            wrap.appendChild(createFlatMessage());
            return;
        }
        appendBreadcrumb(wrap, paths, nodeId, "live");
    });
}

export function renderTrainingNavigation(paths, nodeId, isFlat = false) {
    const nav = document.getElementById("navigator");
    if (!nav) return;
    clearDiv(nav);
    const wrap = createNavWrap(nav);

    const hasPaths = Array.isArray(paths) && paths.length > 0;
    const parsed = hasPaths ? parseNavigation(paths, nodeId) : {root: {name: "Shaper"}};
    wrap.appendChild(buildShaperPicker("training", nodeId, parsed.root.name));

    if (!hasPaths) {
        const empty = document.createElement("span");
        empty.className = "text-muted small";
        empty.textContent = "Training navigation unavailable.";
        wrap.appendChild(empty);
        return;
    }

    if (isFlat) {
        wrap.appendChild(createFlatMessage());
        return;
    }

    appendBreadcrumb(wrap, paths, nodeId, "training");
}
